import React, { useEffect, useState } from 'react';
import Nav from '../components/Nav/Nav';
import '../App.css';
import { useParams, useNavigate } from 'react-router-dom';
import { RightOutlined } from '@ant-design/icons';
import { LuShare } from "react-icons/lu";

import { useAuth } from '../contexts/AuthContext';
import {
  createConnectedAccount,
  createOnboardingLink,
  createStripeLoginLink,
  getStripeAccountStatus,
} from '../api/backend';

function Restaurant() {
  const navigate = useNavigate();
  const { id } = useParams();

  const {
    getRestaurant,
    currentRestaurant,
    currentUser,
    isLoading,
    userProfile,
    updateRestaurantStripeData,
  } = useAuth();

  const [stripeLoading, setStripeLoading] = useState(false);
  const [stripeSyncLoading, setStripeSyncLoading] = useState(false);
  const [openShare, setOpenShare] = useState(false);

  const stripeDetailsSubmitted = !!currentRestaurant?.stripeDetailsSubmitted;
  const stripeChargesEnabled = !!currentRestaurant?.stripeChargesEnabled;
  const stripePayoutsEnabled = !!currentRestaurant?.stripePayoutsEnabled;
  const stripeOnboardingCompleted = !!currentRestaurant?.stripeOnboardingCompleted;
  const isDigitalProduct = currentRestaurant?.productType === "digital";
  const hasStripeAccount = !!currentRestaurant?.stripeAccountId;

  const isStripeFullyReady =
    stripeDetailsSubmitted &&
    stripeChargesEnabled &&
    stripePayoutsEnabled &&
    stripeOnboardingCompleted;

  const stripeButtonLabel = !hasStripeAccount
    ? "Configurer Stripe"
    : isStripeFullyReady
    ? "Ouvrir Stripe"
    : "Terminer la configuration";


  

  useEffect(() => {
    getRestaurant(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (
      !isLoading &&
      currentRestaurant &&
      currentUser &&
      currentRestaurant.owner !== currentUser.uid
    ) {
      navigate('/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, currentRestaurant, currentUser]);

  useEffect(() => {
    const syncStripeStatus = async () => {
      if (
        !currentRestaurant?.id ||
        !currentRestaurant?.stripeAccountId ||
        stripeSyncLoading
      ) {
        return;
      }

      try {
        setStripeSyncLoading(true);

        const status = await getStripeAccountStatus(currentRestaurant.stripeAccountId);

        const nextStripeData = {
          stripeOnboardingCompleted:
            !!status.detailsSubmitted || !!status.chargesEnabled,
          stripeChargesEnabled: !!status.chargesEnabled,
          stripePayoutsEnabled: !!status.payoutsEnabled,
          stripeDetailsSubmitted: !!status.detailsSubmitted,
        };

        const hasChanged =
          currentRestaurant?.stripeOnboardingCompleted !== nextStripeData.stripeOnboardingCompleted ||
          currentRestaurant?.stripeChargesEnabled !== nextStripeData.stripeChargesEnabled ||
          currentRestaurant?.stripePayoutsEnabled !== nextStripeData.stripePayoutsEnabled ||
          currentRestaurant?.stripeDetailsSubmitted !== nextStripeData.stripeDetailsSubmitted;

        if (hasChanged) {
          await updateRestaurantStripeData(currentRestaurant.id, nextStripeData);
        }
      } catch (error) {
        console.log('syncStripeStatus error:', error);
      } finally {
        setStripeSyncLoading(false);
      }
    };

    syncStripeStatus();
  }, [
    currentRestaurant?.id,
    currentRestaurant?.stripeAccountId,
    currentRestaurant?.stripeOnboardingCompleted,
    currentRestaurant?.stripeChargesEnabled,
    currentRestaurant?.stripePayoutsEnabled,
    currentRestaurant?.stripeDetailsSubmitted,
    stripeSyncLoading,
    updateRestaurantStripeData,
  ]);

  const handleStripeClick = async () => {
    if (!currentRestaurant || !currentUser) return;
  
    try {
      setStripeLoading(true);
  
      let accountId = currentRestaurant?.stripeAccountId || null;
  
      if (!accountId) {
        const created = await createConnectedAccount({
          email: userProfile.email,
          country: "FR",
        });
  
        accountId = created.accountId;
  
        await updateRestaurantStripeData(currentRestaurant.id, {
          stripeAccountId: accountId,
          stripeOnboardingCompleted: false,
          stripeChargesEnabled: false,
          stripePayoutsEnabled: false,
          stripeDetailsSubmitted: false,
        });
  
        const onboarding = await createOnboardingLink(accountId, currentRestaurant.id);
  
        window.location.href = onboarding.url; // ✅ DIRECT
        return;
      }
  
      const status = await getStripeAccountStatus(accountId);
  
      const isFullyReady =
        status.detailsSubmitted &&
        status.chargesEnabled &&
        status.payoutsEnabled;
  
      if (isFullyReady) {
        const loginLink = await createStripeLoginLink(accountId);
        window.location.href = loginLink.url; // ✅ DIRECT
        return;
      }
  
      const onboarding = await createOnboardingLink(accountId, currentRestaurant.id);
      window.location.href = onboarding.url; // ✅ DIRECT
  
    } catch (error) {
      console.log(error);
      alert(error.message || "Impossible d’ouvrir Stripe");
    } finally {
      setStripeLoading(false);
    }
  };

  const copyLink = async () => {
    const link = `${window.location.origin}/shop/${id}/menu`;
    await navigator.clipboard.writeText(link);
    setOpenShare(false);
  };

  return (
    <div className="one-page">
      <Nav />

      <div className="main">
        <div className="wrapper-container">
          {currentRestaurant && (
            <div className="wrapper-content">
              <div className="title-wrapper">
                <div>
                  <p>{currentRestaurant.name}</p>
                  <p className="grey">{currentRestaurant.adress}</p>
                </div>

                <div className="title-wrapper-right">
                  <button
                    className="button"
                    onClick={handleStripeClick}
                    disabled={stripeLoading}
                  >
                    {stripeLoading ? "Chargement..." : stripeButtonLabel}
                  </button>

                  <button
                    className="button title-button-secondary"
                    onClick={() =>
                      window.open(
                        `/shop/${id}/menu`,
                        '_blank',
                        'noopener,noreferrer'
                      )
                    }
                  >
                    Voir la boutique
                  </button>

                  <button
                    className="button title-button-secondary"
                    type="button"
                    onClick={() => setOpenShare(true)}
                  >
                    <LuShare />
                  </button>

                  {openShare && (
                    <div className="notif-overlay" onClick={() => setOpenShare(false)}>
                      <div
                        className="notif-modal"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <h2 className="notif-title">Partager la boutique</h2>
                        <p className="notif-subtitle">
                          Partagez votre boutique sur vos réseaux ou envoyez le lien directement à vos clients.
                        </p>

<div className="notif-actions">
                          <button className="notif-btn primary" onClick={copyLink}>
                            Copier le lien
                          </button>
                          <button className="notif-btn secondary" onClick={() => setOpenShare(false)}>
                            Fermer
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="wrapper" style={{ padding: 0 }}>
                <div
                  className="card-item restaurant"
                  onClick={() => navigate(`/shop/${id}/products`)}
                >
                  <p>Produits</p>
                  <RightOutlined className="link2" />
                </div>

                <div
                  className="card-item restaurant"
                  onClick={() => navigate(`/shop/${id}/categories`)}
                >
                  <p>Catégories</p>
                  <RightOutlined className="link2" />
                </div>

                {isStripeFullyReady && !isDigitalProduct && (
  <div
    className="card-item restaurant"
    onClick={() => navigate(`/shop/${id}/orders`)}
  >
    <p>Gestion des commandes</p>
    <RightOutlined className="link2" />
  </div>
)}

                {isStripeFullyReady && (
                  <div
                    className="card-item restaurant"
                    onClick={() => navigate(`/shop/${id}/orders-history`)}
                  >
                    <p>Historique de commandes</p>
                    <RightOutlined className="link2" />
                  </div>
                )}

                {isStripeFullyReady && (
                  <div
                    className="card-item restaurant"
                    onClick={() => navigate(`/shop/${id}/payment-settings`)}
                  >
                    <p>Paiements et livraison</p>
                    <RightOutlined className="link2" />
                  </div>
                )}

                <div
                  className="card-item restaurant"
                  onClick={() => navigate(`/shop/${id}/informations`)}
                >
                  <p>Informations</p>
                  <RightOutlined className="link2" />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Restaurant;